import {
  Archive,
  BellRing,
  CheckCircle2,
  FileText,
  Lock,
  Mail,
  ScrollText,
  Settings2,
  ShieldAlert,
} from 'lucide-react'

import Seo from '@/layout/Seo'
import { RootLayout } from '@/studio/RootLayout'
import { StatList, StatListItem } from '@/studio/StatList'
import {
  BentoCard,
  Button,
  ComplianceCard,
  Container,
  Footer,
  Gradient,
  Heading,
  Lead,
  Link,
  LogoCloud,
  Navbar,
  StatusBadge,
  Subheading,
  TierCard,
} from '@/radiant'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import {
  CompliancePosture,
  DeterministicReasoning,
} from '@/radiant/illustrations'

function HeroPanel() {
  // A product panel that makes the compliance posture tangible in the first
  // screen (Stage 9.4). The CompliancePosture illustration + two live-looking
  // vendor rows read as a genuine dashboard fragment — real product presence,
  // not a lone icon. Illustrative data only.
  return (
    <div className="rounded-2xl bg-white/95 p-5 shadow-xl ring-1 ring-black/5 backdrop-blur-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-[10px] font-semibold tracking-[0.2em] text-gray-500 uppercase">
            Compliance dashboard
          </p>
          <p className="mt-0.5 text-sm font-semibold text-gray-950">
            Riverside Property Group
          </p>
        </div>
        <StatusBadge status="compliant" label="90% compliant" />
      </div>
      <div className="mt-4 border-y border-gray-100 py-3">
        <CompliancePosture />
      </div>
      <ul className="mt-3 space-y-2">
        {[
          { v: 'Summit Mechanical LLC', s: 'expiring' as const, d: 'GL expires in 15 days' },
          { v: 'Delta Electric Co.', s: 'action-required' as const, d: 'Umbrella below required limit' },
        ].map((row) => (
          <li
            key={row.v}
            className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2 ring-1 ring-black/5"
          >
            <div className="min-w-0">
              <p className="truncate text-xs font-medium text-gray-900">
                {row.v}
              </p>
              <p className="truncate text-[11px] text-gray-500">{row.d}</p>
            </div>
            <StatusBadge status={row.s} />
          </li>
        ))}
      </ul>
    </div>
  )
}

function Hero() {
  return (
    <div className="relative">
      <Gradient className="absolute inset-2 bottom-0 rounded-4xl ring-1 ring-black/5 ring-inset" />
      <Container className="relative">
        <Navbar />
        <div className="grid grid-cols-1 items-center gap-12 pt-16 pb-24 sm:pt-24 sm:pb-32 md:pt-28 md:pb-40 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-7">
            <p className="text-base/7 font-semibold text-gray-950/75">
              Vendor compliance monitoring for property managers and real estate
              operators
            </p>
            {/* Scale steps down a notch in the split layout so the headline
                keeps a 2-line lockup beside the product panel (Stage 9.4). */}
            <h1 className="mt-6 font-display text-5xl/[0.95] font-medium tracking-tight text-balance text-gray-950 sm:text-6xl/[0.9] lg:text-7xl/[0.9]">
              Vendor compliance, built like infrastructure.
            </h1>
            <p className="mt-8 max-w-2xl text-xl/8 font-medium text-gray-950 sm:text-2xl/8">
              Automated COI tracking and defensible compliance records for
              property managers and real estate operators.
            </p>
            <p className="mt-6 max-w-2xl text-base/7 text-gray-950/75 sm:text-lg/7">
              CertShieldPro evaluates every vendor's coverage against your
              requirements, eliminates spreadsheet tracking, and produces
              defensible compliance records — for renewals, audits, or lender
              reviews.
            </p>
            <div className="mt-10 flex flex-col gap-x-6 gap-y-4 sm:flex-row">
              <Button href="/login">Start Free Trial</Button>
              <Button variant="secondary" href="/how-it-works">
                See how it works
              </Button>
            </div>
            <p className="mt-6 text-sm/6 text-gray-950/65">
              14-day free trial · No credit card required · Cancel anytime
            </p>
          </div>
          <div className="lg:col-span-5">
            <HeroPanel />
          </div>
        </div>
      </Container>
    </div>
  )
}

function DesignedFor() {
  return (
    <Container className="mt-16 sm:mt-24">
      <Subheading className="text-center">
        Designed for compliance teams in
      </Subheading>
      <LogoCloud className="mt-10" />
    </Container>
  )
}

// --- Dark Bento (Section 3) graphics -----------------------------------------

function GLEvaluationGraphic() {
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8">
      <ComplianceCard
        className="w-full max-w-sm"
        vendor="Summit Mechanical LLC"
        status="action-required"
        lines={[
          {
            type: 'General Liability',
            found: '$1M / $2M',
            required: '$1M / $2M',
            status: 'compliant',
          },
          {
            type: 'Umbrella / Excess',
            found: '$1M',
            required: '$2M',
            status: 'action-required',
            reason: 'Limit below requirement — needs $1M additional excess.',
          },
        ]}
      />
    </div>
  )
}

function RequirementsToggleGraphic() {
  const rows = [
    { label: 'General Liability', on: true },
    { label: 'Auto Liability', on: true },
    { label: "Workers' Comp", on: true },
    { label: 'Umbrella / Excess', on: false },
  ]
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8">
      <div className="w-full max-w-xs space-y-3 rounded-xl bg-white p-5 shadow-md ring-1 ring-black/5">
        <p className="text-xs font-semibold text-gray-500">
          Requirements · Property Management
        </p>
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between gap-3"
          >
            <span className="text-sm font-medium text-gray-900">
              {row.label}
            </span>
            <span
              className={`flex h-5 w-9 items-center rounded-full px-0.5 ${row.on ? 'justify-end bg-indigo-600' : 'justify-start bg-gray-200'}`}
            >
              <span className="size-4 rounded-full bg-white" />
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function AuditLogGraphic() {
  const entries = [
    { who: 'System', what: 'Evaluated COI · Summit Mechanical', when: '2m ago' },
    { who: 'A. Reyes', what: 'Override: GL limit verified', when: '1h ago' },
    { who: 'System', what: 'Exported compliance report', when: 'Yesterday' },
  ]
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8">
      <div className="w-full max-w-xs space-y-2 rounded-xl bg-white p-5 shadow-md ring-1 ring-black/5">
        <p className="text-xs font-semibold text-gray-500">Audit log</p>
        {entries.map((entry, i) => (
          <div
            key={i}
            className="flex items-start gap-3 border-t border-gray-100 pt-2 first:border-0 first:pt-0"
          >
            <ScrollText
              className="mt-0.5 size-4 shrink-0 text-indigo-600"
              aria-hidden="true"
            />
            <div className="min-w-0">
              <p className="truncate text-xs font-medium text-gray-900">
                {entry.what}
              </p>
              <p className="text-[11px] text-gray-500">
                {entry.who} · {entry.when}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function FailReasoningGraphic() {
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8">
      <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-md ring-1 ring-black/5">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-900">
            Umbrella / Excess
          </span>
          <StatusBadge status="action-required" label="FAIL" />
        </div>
        <dl className="mt-4 space-y-2 text-xs">
          <div className="flex justify-between">
            <dt className="text-gray-500">Requirement</dt>
            <dd className="tnum font-medium text-gray-900">$2,000,000</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Found on COI</dt>
            <dd className="tnum font-medium text-gray-900">$1,000,000</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Source</dt>
            <dd className="tnum font-medium text-gray-900">ACORD 25, line 16</dd>
          </div>
        </dl>
        <p className="mt-4 rounded-lg bg-amber-50 p-3 text-xs text-amber-800 ring-1 ring-amber-600/20 ring-inset">
          Would PASS with $1,000,000 additional excess coverage.
        </p>
      </div>
    </div>
  )
}

function ComplianceReasoning() {
  return (
    <div className="mt-24 bg-gray-900 py-32 sm:mt-32">
      <Container>
        <Subheading dark>Deterministic evaluation</Subheading>
        <Heading as="h2" dark className="mt-2 max-w-3xl">
          Every coverage line, evaluated against your requirements.
        </Heading>
        <p className="mt-6 max-w-3xl text-lg/7 text-gray-400">
          CertShieldPro doesn't score vendors with AI guesswork. It extracts each
          policy limit, expiration date, and endorsement, then compares them
          against the requirements you configure. Every PASS or FAIL has a
          documented reason you can defend to your lender, auditor, or risk
          committee.
        </p>
        <div className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-6 lg:grid-rows-2">
          <BentoCard
            dark
            eyebrow="Transparency"
            title="See the work, not just the answer"
            description="Each evaluation shows the source COI document side-by-side with extracted values. Override any field manually — overrides are logged."
            graphic={<GLEvaluationGraphic />}
            className="lg:col-span-4 lg:rounded-tl-4xl"
          />
          <BentoCard
            dark
            eyebrow="Configuration"
            title="Configure once, evaluate forever"
            description="Define your coverage requirements once. Every new COI gets evaluated against them automatically — no per-vendor setup."
            graphic={<RequirementsToggleGraphic />}
            className="lg:col-span-2 lg:rounded-tr-4xl"
          />
          <BentoCard
            dark
            eyebrow="Audit trail"
            title="Audit-ready by default"
            description="Every decision logged with timestamp, source, and reasoning. Export to PDF or CSV for compliance reviews."
            graphic={<AuditLogGraphic />}
            className="lg:col-span-2 lg:rounded-bl-4xl"
          />
          <BentoCard
            dark
            eyebrow="Defensibility"
            title="Reasoning your reviewers will accept"
            description="When a policy limit is $1M and your requirement is $2M, we don't just say 'FAIL.' We tell you why, where in the COI we found it, and what would make it PASS."
            graphic={<FailReasoningGraphic />}
            className="lg:col-span-4 lg:rounded-br-4xl"
          />
        </div>
      </Container>
    </div>
  )
}

// --- Light Bento (Section 4) graphics ----------------------------------------
// Product-flavored mocks (D3): each graphic shows what the feature actually
// produces, in the same visual vocabulary as the dark-bento ComplianceCards.
// Illustrative data only — never real customers.

function EmailIntakeGraphic() {
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8">
      <div className="w-full max-w-sm space-y-3">
        <div className="rounded-xl bg-white p-4 shadow-md ring-1 ring-black/5">
          <div className="flex items-start gap-3">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
              <Mail className="size-5" aria-hidden="true" />
            </span>
            <div className="min-w-0 text-left">
              <p className="truncate text-xs text-gray-500">
                From: renewals@summitinsurance.com
              </p>
              <p className="truncate text-sm font-medium text-gray-900">
                COI — Summit Mechanical LLC
              </p>
              <span className="mt-2 inline-flex max-w-full items-center gap-1.5 rounded-md bg-gray-50 px-2 py-1 text-xs text-gray-600 ring-1 ring-black/5">
                <FileText className="size-3.5 shrink-0 text-gray-400" aria-hidden="true" />
                <span className="truncate">Summit-Mechanical-2026.pdf</span>
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-xl bg-gray-950 px-4 py-3">
          <CheckCircle2 className="size-4 shrink-0 text-indigo-400" aria-hidden="true" />
          <p className="truncate text-xs font-medium text-white">
            Routed to Summit Mechanical LLC — extraction queued
          </p>
        </div>
      </div>
    </div>
  )
}

function ZipUploadGraphic() {
  const files = [
    { file: 'Apex-Roofing-2026.pdf', vendor: 'Apex Roofing', matched: true },
    { file: 'Delta-Electric-COI.pdf', vendor: 'Delta Electric Co.', matched: true },
    { file: 'scan_0043.pdf', vendor: 'Review match', matched: false },
  ]
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8">
      <div className="w-full max-w-sm rounded-xl bg-white p-4 shadow-md ring-1 ring-black/5">
        <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
          <Archive className="size-4 text-indigo-600" aria-hidden="true" />
          <p className="text-sm font-medium text-gray-900">vendor-program.zip</p>
          <span className="ml-auto text-xs text-gray-500">24 documents</span>
        </div>
        <ul className="divide-y divide-gray-100">
          {files.map((row) => (
            <li key={row.file} className="flex items-center gap-2 py-2.5">
              <span className="min-w-0 flex-1 truncate text-xs text-gray-600">
                {row.file}
              </span>
              <span
                aria-hidden="true"
                className="shrink-0 text-xs text-gray-400"
              >
                →
              </span>
              <span
                className={`shrink-0 text-xs font-medium ${row.matched ? 'text-gray-900' : 'text-amber-700'}`}
              >
                {row.vendor}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

function RenewalTimelineGraphic() {
  const reminders = [
    { label: '60d', state: 'Sent' },
    { label: '30d', state: 'Sent' },
    { label: '15d', state: 'Scheduled' },
  ]
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8">
      <div className="w-full max-w-xs rounded-xl bg-white p-5 shadow-md ring-1 ring-black/5">
        <div className="flex items-center gap-2">
          <BellRing className="size-4 shrink-0 text-indigo-600" aria-hidden="true" />
          <p className="truncate text-sm font-medium text-gray-900">
            Summit Mechanical
          </p>
        </div>
        <p className="mt-1 text-xs text-gray-500">GL expires 09/30/2026</p>
        <div className="mt-4 flex items-center gap-2">
          {reminders.map((r, i) => (
            <div key={r.label} className="flex flex-1 items-center gap-2">
              <div className="flex-1 rounded-lg bg-gray-50 px-2 py-2 text-center ring-1 ring-black/5">
                <p className="text-xs font-semibold text-gray-900">{r.label}</p>
                <p
                  className={`text-[10px] ${r.state === 'Sent' ? 'text-emerald-700' : 'text-gray-500'}`}
                >
                  {r.state}
                </p>
              </div>
              {i < reminders.length - 1 && (
                <span aria-hidden="true" className="text-gray-300">
                  ·
                </span>
              )}
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-gray-500">
          Vendor portal link included — no account required
        </p>
      </div>
    </div>
  )
}

function RecordsGraphic() {
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8">
      <div className="w-full max-w-xs rounded-xl bg-white p-5 shadow-md ring-1 ring-black/5">
        <div className="flex items-center justify-between border-b border-gray-100 pb-3">
          <div>
            <p className="text-sm font-semibold text-gray-950">
              Compliance Report
            </p>
            <p className="text-xs text-gray-500">Q3 2026 · 142 vendors</p>
          </div>
          <FileText className="size-5 text-indigo-600" aria-hidden="true" />
        </div>
        <dl className="mt-3 space-y-1.5 text-xs">
          <div className="flex justify-between">
            <dt className="text-gray-500">Compliant</dt>
            <dd className="font-medium text-emerald-700">128</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Expiring soon</dt>
            <dd className="font-medium text-amber-700">9</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Action required</dt>
            <dd className="font-medium text-red-700">5</dd>
          </div>
        </dl>
        <div className="mt-4 flex gap-2">
          {['PDF', 'CSV', 'JSON'].map((fmt) => (
            <span
              key={fmt}
              className="rounded-md bg-gray-50 px-2 py-1 text-[10px] font-semibold tracking-wide text-gray-600 ring-1 ring-black/5"
            >
              {fmt}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

function GapDetectionGraphic() {
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8">
      <div className="flex w-full max-w-xs flex-col gap-2 rounded-xl bg-white p-5 shadow-md ring-1 ring-black/5">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-900">Apex Roofing</span>
          <StatusBadge status="compliant" />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-900">
            Summit Mechanical
          </span>
          <StatusBadge status="expiring" />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-900">
            Delta Electric
          </span>
          <StatusBadge status="action-required" />
        </div>
      </div>
    </div>
  )
}

function CoreCapabilities() {
  return (
    <Container className="mt-24 sm:mt-32">
      <Subheading>Built for real operations</Subheading>
      <Heading as="h2" className="mt-2 max-w-3xl">
        Everything you need to monitor vendor compliance at scale.
      </Heading>
      <div className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-6 lg:grid-rows-2">
        <BentoCard
          eyebrow="Intake"
          title="Email intake, zero vendor friction"
          description="Vendors forward their COIs to a dedicated address. Our extraction engine routes documents to the right vendor record automatically. No vendor login required."
          graphic={<EmailIntakeGraphic />}
          fade={['bottom']}
          className="lg:col-span-3 lg:rounded-tl-4xl"
        />
        <BentoCard
          eyebrow="Bulk import"
          title="Bulk ZIP upload"
          description="Drag-and-drop an entire vendor program at once. Smart matching pairs documents to existing vendor records."
          graphic={<ZipUploadGraphic />}
          fade={['bottom']}
          className="lg:col-span-3 lg:rounded-tr-4xl"
        />
        <BentoCard
          eyebrow="Renewals"
          title="Renewal automation"
          description="Automated reminders at 60, 30, and 15 days before expiration. Vendors get a portal link with no account required."
          graphic={<RenewalTimelineGraphic />}
          className="lg:col-span-2 lg:rounded-bl-4xl"
        />
        <BentoCard
          eyebrow="Records"
          title="Defensible compliance records"
          description="Generate compliance reports formatted in standard documentation patterns — ready for handoff to lenders, auditors, or risk committees."
          graphic={<RecordsGraphic />}
          className="lg:col-span-2"
        />
        <BentoCard
          eyebrow="Coverage gaps"
          title="Coverage gap detection"
          description="Identify vendors missing required coverage types or with expired endorsements — before your audit does."
          graphic={<GapDetectionGraphic />}
          className="lg:col-span-2 lg:rounded-br-4xl"
        />
      </div>
    </Container>
  )
}

// --- Section 5 — Before / After ----------------------------------------------

function BeforeAfter() {
  const oldWay = [
    "Every vendor's COI tracked in a shared spreadsheet",
    'Manual review of each renewal — hours per vendor per year',
    'Compliance gaps discovered during audits, not before',
    'Review requests trigger weeks of document gathering',
    'No defensible record of when, why, or by whom decisions were made',
  ]
  const newWay = [
    "Every vendor's COI extracted, evaluated, and tracked automatically",
    'Renewals trigger automatic notifications and evaluations',
    'Coverage gaps surfaced the moment they appear',
    'Compliance reports generated in one click',
    'Immutable audit log of every evaluation, override, and decision',
  ]
  return (
    <Container className="mt-24 sm:mt-32">
      <Subheading>The shift</Subheading>
      <Heading as="h2" className="mt-2 max-w-3xl">
        From spreadsheet maintenance to compliance infrastructure.
      </Heading>
      <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-3xl bg-gray-50 p-8 ring-1 ring-black/5">
          <h3 className="text-lg font-semibold text-gray-950">
            Manual COI tracking
          </h3>
          <ul className="mt-6 space-y-4">
            {oldWay.map((item) => (
              <li key={item} className="flex gap-3 text-sm/6 text-gray-600">
                <span
                  aria-hidden="true"
                  className="mt-2 size-1.5 shrink-0 rounded-full bg-gray-400"
                />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-3xl bg-gray-950 p-8 ring-1 ring-black/5">
          <h3 className="text-lg font-semibold text-white">
            Automated compliance infrastructure
          </h3>
          <ul className="mt-6 space-y-4">
            {newWay.map((item) => (
              <li key={item} className="flex gap-3 text-sm/6 text-gray-300">
                <CheckCircle2
                  className="mt-0.5 size-5 shrink-0 text-indigo-400"
                  aria-hidden="true"
                />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Container>
  )
}

// --- Section 6 — Activation strip --------------------------------------------

function ActivationStrip() {
  const steps = [
    {
      phase: 'First',
      title: 'Configure requirements',
      description:
        'Set your minimum coverage limits (GL, Auto, WC, Umbrella, Professional). Choose templates by org type.',
    },
    {
      phase: 'Next',
      title: 'Add vendors',
      description:
        'Upload a CSV of your vendor list, or add vendors one-by-one.',
    },
    {
      phase: 'Then',
      title: 'First COIs flow through',
      description:
        'Email a few COIs to your intake address, or drag-and-drop a ZIP. Extraction runs automatically.',
    },
    {
      phase: 'Immediately',
      title: 'See your posture',
      description:
        'Full compliance dashboard with PASS/FAIL by vendor. Drill into any evaluation. Export when ready.',
    },
  ]
  return (
    <Container className="mt-24 sm:mt-32">
      <Subheading>Your first session</Subheading>
      <Heading as="h2" className="mt-2 max-w-3xl">
        From signup to compliance posture, quickly.
      </Heading>
      <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {steps.map((step, i) => (
          <div key={step.title} className="relative">
            <div className="flex size-10 items-center justify-center rounded-full bg-gray-950 text-sm font-semibold text-white">
              {i + 1}
            </div>
            <p className="mt-4 font-mono text-xs font-semibold tracking-widest text-gray-500 uppercase">
              {step.phase}
            </p>
            <h3 className="mt-1 text-lg font-semibold text-gray-950">
              {step.title}
            </h3>
            <p className="mt-2 text-sm/6 text-gray-600">{step.description}</p>
          </div>
        ))}
      </div>
      <div className="mt-12 flex flex-col items-start gap-4">
        <Button href="/login">Start Free Trial</Button>
        <p className="text-sm/6 text-gray-500">
          14-day free trial · No credit card required
        </p>
      </div>
    </Container>
  )
}

// --- Section 7 — Infrastructure proof points ---------------------------------

function InfrastructureProof() {
  return (
    <Container className="mt-24 sm:mt-32">
      <Subheading>Built on real infrastructure</Subheading>
      <Heading as="h2" className="mt-2 max-w-3xl">
        Defensible by design.
      </Heading>
      <StatList className="mt-12">
        <StatListItem
          value="AWS"
          label="Hosted infrastructure — US-East region, isolated environment, automatic failover across availability zones"
        />
        <StatListItem
          value="AES-256"
          label="Encryption with AWS KMS — encrypted at rest with AWS-managed keys, key rotation policies in place, TLS 1.3 in transit"
        />
        <StatListItem
          value="Deterministic"
          label="Every evaluation — documented PASS or FAIL with reasoning for every line of coverage. Defensible by design."
        />
        <StatListItem
          value="Immutable"
          label="Audit log — tamper-evident record at the database layer. Append-only with UPDATE/DELETE revoked from application roles."
        />
      </StatList>
    </Container>
  )
}

// --- Section 8 — Why deterministic evaluation matters ------------------------

function WhyDeterministic() {
  const items = [
    {
      icon: Settings2,
      title: 'What deterministic actually means',
      body: "CertShieldPro doesn't use AI scoring or fuzzy matching to decide whether a vendor is compliant. It compares specific values — policy limits, expiration dates, named insureds, endorsements — against the requirements you've configured. Every evaluation produces an explicit PASS or FAIL based on rules you can read.",
    },
    {
      icon: ShieldAlert,
      title: 'Why it matters for defensibility',
      body: 'When a compliance review asks "why is this vendor marked compliant?" — you can show the rule that was applied, the value that was found, and the source document that contained it. AI-scored systems can\'t answer that question with specificity. Deterministic systems can.',
    },
    {
      icon: Lock,
      title: 'Where AI helps anyway',
      body: 'CertShieldPro uses AI (specifically AWS Textract) for one job: extracting values from COI documents. That\'s a pattern-recognition problem where AI excels. But the decision about whether those extracted values meet your requirements is deterministic, rule-based, and inspectable. AI for extraction, rules for evaluation.',
    },
  ]
  return (
    <Container className="mt-24 sm:mt-32">
      <div className="mx-auto max-w-4xl text-center">
        <Subheading>Why this matters</Subheading>
        <Heading as="h2" className="mt-2">
          Deterministic evaluation, explained.
        </Heading>
      </div>
      {/* The differentiation graphic: the same requirement-vs-found comparison
          the copy describes, made visible (Stage 9.2). */}
      <div className="mx-auto mt-12 max-w-4xl rounded-3xl bg-gray-50 p-6 ring-1 ring-black/5 sm:p-8">
        <DeterministicReasoning />
      </div>
      <div className="mx-auto mt-12 max-w-4xl space-y-12">
        {items.map((item) => (
          <div key={item.title} className="border-t border-gray-200 pt-8">
            <span className="flex size-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 ring-1 ring-indigo-600/10">
              <item.icon className="size-5" aria-hidden="true" />
            </span>
            <h3 className="mt-4 text-xl font-semibold text-gray-950">
              {item.title}
            </h3>
            <p className="mt-3 text-lg/8 text-gray-600">{item.body}</p>
          </div>
        ))}
      </div>
    </Container>
  )
}

// --- Section 9 — Pricing teaser ----------------------------------------------

function PricingTeaser() {
  return (
    <Container className="mt-24 sm:mt-32">
      <Subheading>Transparent pricing</Subheading>
      <Heading as="h2" className="mt-2 max-w-3xl">
        Pricing that scales with your vendor program.
      </Heading>
      <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <TierCard
          name="Starter"
          description="For small portfolios getting structured."
          price="$149"
          vendorCap="Up to 50 active vendors"
          features={[
            'Email + ZIP intake',
            'Default compliance templates',
            'Renewal notifications',
            'PDF + CSV exports',
          ]}
          ctaLabel="Start Free Trial"
          ctaHref="/login"
        />
        <TierCard
          name="Professional"
          description="Most popular for growing operators."
          price="$349"
          vendorCap="Up to 200 active vendors"
          badge="Most popular"
          highlighted
          features={[
            'Everything in Starter',
            'Custom requirements engine',
            'Vendor categorization',
            'Compliance report templates',
          ]}
          ctaLabel="Start Free Trial"
          ctaHref="/login"
        />
        <TierCard
          name="Business"
          description="For mid-market operators."
          price="$799"
          vendorCap="Up to 1,000 active vendors"
          features={[
            'Everything in Professional',
            'Multi-org support',
            'White-label vendor portals',
            'Scheduled exports',
          ]}
          ctaLabel="Start Free Trial"
          ctaHref="/login"
        />
      </div>
      <div className="mt-10 space-y-2 text-sm/6 text-gray-600">
        <p>All plans include 14-day free trial · No credit card · Cancel anytime</p>
        <p>
          Managing 1,000+ vendors?{' '}
          <Link
            href="/enterprise"
            className="group font-medium text-indigo-700 underline underline-offset-4 hover:text-indigo-900"
          >
            See our Enterprise tier{' '}
            <span
              aria-hidden="true"
              className="inline-block transition-transform group-hover:translate-x-1"
            >
              →
            </span>
          </Link>
        </p>
        <p>
          Full feature comparison{' '}
          <Link
            href="/pricing"
            className="group font-medium text-indigo-700 underline underline-offset-4 hover:text-indigo-900"
          >
            available on the pricing page{' '}
            <span
              aria-hidden="true"
              className="inline-block transition-transform group-hover:translate-x-1"
            >
              →
            </span>
          </Link>
        </p>
      </div>
    </Container>
  )
}

// --- Section 10 — FAQ --------------------------------------------------------

const homepageFaqs = [
  {
    q: 'What makes CertShieldPro different from other vendor compliance tools?',
    a: "Two differences worth noting. First, our pricing is published — no demo gauntlet to get a number. Second, our evaluation is deterministic, not AI-scored. Every PASS or FAIL has a documented reason. We're built for compliance teams that need to defend their records, not just score vendors.",
  },
  {
    q: 'Do I need to talk to a salesperson to get started?',
    a: "No. Sign up, configure your requirements, add your vendors, and start receiving COIs in your first session. We don't do demo calls for self-serve customers. Enterprise programs over 1,000 vendors can request a consultation, but it's optional.",
  },
  {
    q: 'What document formats do you support?',
    a: 'PDF (text and scanned), JPG, PNG, and TIFF. Our extraction engine handles both digital-native COIs from carriers and scanned versions from brokers. Multi-page documents and bundled ZIPs are supported.',
  },
  {
    q: 'How accurate is the extraction?',
    a: 'Our extraction engine handles ACORD 25 forms with high accuracy on text-native PDFs. Scanned documents are processed via OCR with manual review surfaced for edge cases. Every extracted value is verifiable against the source document — you can override any field, and overrides are tracked in the audit log.',
  },
  {
    q: 'What happens at the end of the 14-day trial?',
    a: "Your data remains accessible in read-only mode. You can upgrade to a paid plan at any time and pick up exactly where you left off. We won't charge you automatically — upgrading is your decision.",
  },
  {
    q: 'Is CertShieldPro SOC 2 compliant?',
    a: "We operate with SOC 2 Trust Services Criteria controls (encryption, access management, change control, monitoring) and are in pre-audit phase for formal SOC 2 Type 1 certification. We're transparent about our compliance posture — see our Security page for current infrastructure and controls.",
  },
  {
    q: 'Can I export my data if I cancel?',
    a: 'Yes, at any time. All vendor data, COI documents, evaluations, and audit logs export as CSV and PDF. You retain access to exports for 30 days after cancellation.',
  },
]

function Faqs() {
  return (
    <Container className="mt-24 sm:mt-32">
      <div className="mx-auto max-w-3xl">
        <div className="text-center">
          <Subheading>Frequently asked</Subheading>
          <Heading as="h2" className="mt-2">
            Questions before you start your trial.
          </Heading>
        </div>
        <Accordion type="single" collapsible className="mt-12 space-y-3">
          {homepageFaqs.map((faq, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="rounded-2xl bg-gray-50 px-6 ring-1 ring-black/5"
            >
              <AccordionTrigger className="text-left text-base font-semibold text-gray-950">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="text-base/7 text-gray-600">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </Container>
  )
}

// --- Section 11 — Final CTA --------------------------------------------------

function FinalCta() {
  return (
    <Container className="mt-24 sm:mt-32">
      {/* Footer-style gradient wash: an even tint across the whole panel
          (the old inset-2 white/90 overlay reduced the gradient to an 8px
          rim that read as a border artifact — D8). */}
      <div className="relative overflow-hidden rounded-4xl ring-1 ring-black/5 ring-inset">
        <Gradient className="absolute inset-0" />
        <div className="absolute inset-0 bg-white/80" />
        <div className="relative px-6 py-24 text-center sm:px-12">
          <Heading as="h2" className="mx-auto max-w-2xl">
            See your vendor compliance posture in minutes.
          </Heading>
          <Lead className="mx-auto mt-6 max-w-2xl">
            Start your 14-day free trial. No credit card required. No sales call.
            Just defensible compliance infrastructure you can show your reviewers
            today.
          </Lead>
          <div className="mt-10 flex flex-col items-center justify-center gap-x-6 gap-y-4 sm:flex-row">
            <Button href="/login">Start Free Trial</Button>
            <Button variant="secondary" href="/enterprise">
              Talk to enterprise
            </Button>
          </div>
        </div>
      </div>
    </Container>
  )
}

export default function Index() {
  return (
    <RootLayout>
      <Seo
        title="CertShieldPro — Vendor compliance, built like infrastructure"
        description="Automated COI tracking and defensible compliance records for property managers and real estate operators. 14-day free trial, no credit card."
        canonical="https://certshieldpro.com"
        ogImage="https://certshieldpro.com/og-image.png"
      />
      <main id="main-content">
        <Hero />
        <DesignedFor />
        <ComplianceReasoning />
        <CoreCapabilities />
        <BeforeAfter />
        <ActivationStrip />
        <InfrastructureProof />
        <WhyDeterministic />
        <PricingTeaser />
        <Faqs />
        <FinalCta />
      </main>
      <Footer />
    </RootLayout>
  )
}
